import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import {
  ScrollText, RefreshCw, Trash2, Calendar as CalendarIcon,
  CalendarX, User, MapPin, ShieldAlert,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import AdminLayout from '../../components/layout/AdminLayout';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// UI metadata for the three cascade-delete action types. Colour + human label
// come from here so the table + detail rows stay in sync.
const ACTION_META = {
  delete_shift: {
    label: 'Single shift',
    icon: Trash2,
    color: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  },
  delete_event_day: {
    label: 'Event day',
    icon: CalendarX,
    color: 'border-orange-500/40 bg-orange-500/10 text-orange-300',
  },
  delete_full_event: {
    label: 'Full event',
    icon: ShieldAlert,
    color: 'border-red-500/40 bg-red-500/10 text-red-300',
  },
};

function fmtAbs(iso) {
  if (!iso) return '—';
  try { return format(parseISO(iso), 'dd MMM yyyy · HH:mm'); }
  catch { return iso; }
}

export default function AdminShiftAuditLogPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | delete_shift | delete_event_day | delete_full_event

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/shift-audit-log?limit=250`);
      setEntries(r.data.entries || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  const filtered = filter === 'all'
    ? entries
    : entries.filter((e) => e.action_type === filter);

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 space-y-6 min-h-screen">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-6">
          <div>
            <h1 className="text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
              <ScrollText className="w-6 h-6 text-primary" strokeWidth={1.5} />
              SHIFT AUDIT LOG
            </h1>
            <p className="text-sm text-muted-foreground mt-1 font-mono">
              Immutable record of every shift, event-day and full-event deletion by admin
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchLog}
            disabled={loading}
            className="border-border rounded-sm"
            data-testid="audit-log-refresh-btn"
          >
            <RefreshCw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} /> Refresh
          </Button>
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap gap-2" data-testid="audit-log-filters">
          {[
            { key: 'all',                label: 'All actions' },
            { key: 'delete_shift',       label: 'Single shift' },
            { key: 'delete_event_day',   label: 'Event day' },
            { key: 'delete_full_event',  label: 'Full event' },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={cn(
                'px-3 py-1 text-xs uppercase tracking-wider rounded-sm border transition-colors',
                filter === f.key
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-transparent border-border text-muted-foreground hover:text-foreground'
              )}
              data-testid={`audit-log-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="text-muted-foreground text-sm py-12 text-center">Loading…</div>
        )}

        {!loading && filtered.length === 0 && (
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-12 text-center text-muted-foreground">
              <ScrollText className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No shift deletions recorded yet.</p>
            </CardContent>
          </Card>
        )}

        {!loading && filtered.length > 0 && (
          <div className="space-y-3" data-testid="audit-log-list">
            {filtered.map((e) => {
              const meta = ACTION_META[e.action_type] || ACTION_META.delete_shift;
              const Icon = meta.icon;
              const employeeCount = (e.affected_employee_names || []).length;
              const dateCount = (e.affected_dates || []).length;
              const shiftCount = (e.deleted_shift_ids || []).length;
              return (
                <Card
                  key={e.id}
                  className="bg-card border-border rounded-sm hover:border-primary/30 transition-colors"
                  data-testid={`audit-log-entry-${e.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className={cn('p-2 rounded-sm border', meta.color)}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <CardTitle className="text-sm font-medium truncate">
                            {e.event_name || '(no location)'}
                          </CardTitle>
                          <p className="text-[11px] text-muted-foreground font-mono mt-0.5">
                            {fmtAbs(e.deleted_at)}
                          </p>
                        </div>
                      </div>
                      <Badge className={cn('uppercase text-[10px] tracking-wider border', meta.color)}>
                        {meta.label}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-2 space-y-2 text-sm">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Admin</p>
                        <p className="font-medium truncate flex items-center gap-1">
                          <User className="w-3 h-3 text-muted-foreground" />
                          {e.admin_name || '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Shifts removed</p>
                        <p className="font-medium">{shiftCount}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Employees affected</p>
                        <p className="font-medium">{employeeCount}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Dates</p>
                        <p className="font-medium">{dateCount}</p>
                      </div>
                    </div>

                    {e.affected_dates?.length > 0 && (
                      <div className="flex flex-wrap gap-1 pt-1">
                        {e.affected_dates.map((d) => (
                          <Badge key={d} variant="outline" className="text-[10px] font-mono">
                            <CalendarIcon className="w-2.5 h-2.5 mr-1" /> {d}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {e.affected_employee_names?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {e.affected_employee_names.slice(0, 8).map((n) => (
                          <Badge key={n} variant="outline" className="text-[10px]">
                            <User className="w-2.5 h-2.5 mr-1" /> {n}
                          </Badge>
                        ))}
                        {e.affected_employee_names.length > 8 && (
                          <Badge variant="outline" className="text-[10px]">
                            +{e.affected_employee_names.length - 8} more
                          </Badge>
                        )}
                      </div>
                    )}

                    {e.preserved_shift_ids?.length > 0 && (
                      <p className="text-[11px] text-amber-400/80 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {e.preserved_shift_ids.length} shift(s) preserved for history (already worked)
                      </p>
                    )}

                    {e.notes && (
                      <p className="text-[11px] text-muted-foreground/80 italic">{e.notes}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
